/**
 * "Budget vs Actual" chart inside the Rehab detail drawer.
 *
 * Renders:
 *   - A headline chip with over/under variance status.
 *   - A total summary bar: a green segment for in-budget spend + a red
 *     overflow segment, with a marker showing the budget line.
 *   - One row per scope item with the same bi-color bar pattern, scaled to
 *     the row's own budget so rows are visually comparable.
 *
 * Mirrors the `.dr-chart-*` markup in design/Syntra/rehab.html. Input is the
 * already-composed `ScopeItem[]` so the chart doesn't have to know whether a
 * row came from `line_items` or `custom_items`.
 *
 * The "actual" side is recognized spend (see `recognizedSpend`): a task counts
 * its recorded actual cost once it's marked completed, and nothing before
 * that. A completed task that ran over its estimate still drives the
 * over-budget / overflow segments.
 */
import { BarChart3 } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  recognizedSpend,
  totalRecognizedSpend,
  type ScopeItem,
  visibleScopeItems,
} from "./rehab-scope";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

interface RehabBudgetChartProps {
  items: ScopeItem[];
}

export function RehabBudgetChart({ items }: RehabBudgetChartProps) {
  // Per-task rows hide line items with no estimated cost (budget 0) — they're
  // empty scope entries and only clutter the list. Totals still span all items.
  const visibleItems = visibleScopeItems(items);
  const totalBudget = items.reduce((s, i) => s + i.budget, 0);
  const totalActual = totalRecognizedSpend(items);
  const variance = totalActual - totalBudget;
  const over = variance > 0;
  const variancePct =
    totalBudget > 0 ? Math.round((Math.abs(variance) / totalBudget) * 100) : 0;

  // Total bar segments: scale to max(budget, actual). Budget marker sits at
  // budget/scale; actual fill splits into green (in-budget) + red (overflow).
  const totalScale = Math.max(totalBudget, totalActual, 1);
  const budgetMarkPct = (totalBudget / totalScale) * 100;
  const actualPct = (totalActual / totalScale) * 100;
  const inBudgetPct = Math.min(actualPct, budgetMarkPct);
  const overflowPct = Math.max(0, actualPct - budgetMarkPct);

  const headlineClass = over
    ? "bg-destructive/15 text-destructive"
    : totalActual > 0
      ? "bg-success/15 text-success"
      : "border border-border bg-card text-muted-foreground";
  const headlineText = over
    ? `${formatCurrency(Math.abs(variance))} over budget (${variancePct}%)`
    : totalActual > 0
      ? variance === 0
        ? "On budget"
        : `${formatCurrency(Math.abs(variance))} under budget`
      : "No spend recorded yet";

  const utilPct =
    totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0;
  const remaining = totalBudget - totalActual;

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <BarChart3 className="h-3.5 w-3.5 text-primary" aria-hidden />
          Budget vs Actual
        </span>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[11px] font-bold",
            headlineClass,
          )}
        >
          {headlineText}
        </span>
      </div>

      {/* Total summary */}
      <div className="mb-4 rounded-lg border border-border bg-card p-3.5">
        <div className="mb-2.5 flex items-baseline justify-between gap-2">
          <span className="flex flex-wrap items-baseline gap-2">
            <span
              className={cn(
                "text-xl font-extrabold leading-none tabular-nums",
                over ? "text-destructive" : "text-foreground",
              )}
            >
              {formatCurrency(totalActual)}
            </span>
            <span className="text-xs text-muted-foreground">
              spent of{" "}
              <b className="font-bold text-foreground">
                {formatCurrency(totalBudget)}
              </b>{" "}
              budget
            </span>
          </span>
        </div>
        <div className="relative h-3 w-full overflow-hidden rounded-full border border-border bg-muted/40">
          <div
            className="absolute top-0 bottom-0 bg-gradient-to-r from-success to-success/60 transition-all"
            style={{ left: 0, width: `${inBudgetPct}%` }}
            aria-hidden
          />
          <div
            className="absolute top-0 bottom-0 bg-gradient-to-r from-destructive to-destructive/60 transition-all"
            style={{ left: `${budgetMarkPct}%`, width: `${overflowPct}%` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -top-0.5 -bottom-0.5 w-0.5 bg-foreground/85 ring-1 ring-background"
            style={{ left: `${budgetMarkPct}%` }}
            aria-label={`Budget marker at ${formatCurrency(totalBudget)}`}
          />
        </div>
        <div className="mt-2 flex items-baseline justify-between text-[11.5px] tabular-nums">
          <span className="text-muted-foreground">
            {totalActual > 0 ? `${utilPct}% of budget used` : "No spend recorded yet"}
          </span>
          <span
            className={cn(
              "font-bold",
              over ? "text-destructive" : "text-success",
            )}
          >
            {over
              ? `+${formatCurrency(variance)} over`
              : `${formatCurrency(Math.max(0, remaining))} remaining`}
          </span>
        </div>
      </div>

      {/* Per-task rows */}
      {visibleItems.length > 0 && (
        <div className="flex flex-col gap-0.5">
          {visibleItems.map((item) => {
            const budget = item.budget;
            // Recognized on completion — a pending task reads $0 here even
            // once an amount has been typed into its Actual column.
            const actual = recognizedSpend(item);
            const rowVariance = actual - budget;
            const rowOver = rowVariance > 0;
            const rowScale = Math.max(budget, actual, 1);
            const rowBudgetPct = (budget / rowScale) * 100;
            const rowActualPct = (actual / rowScale) * 100;
            const rowInBudgetPct = Math.min(rowActualPct, rowBudgetPct);
            const rowOverflowPct = Math.max(0, rowActualPct - rowBudgetPct);
            const dotClass =
              actual <= 0
                ? "bg-border"
                : rowOver
                  ? "bg-destructive"
                  : "bg-success";

            return (
              <div
                key={item.key}
                className="flex flex-col gap-1.5 rounded-lg p-2.5 transition hover:bg-card"
              >
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="flex min-w-0 items-center gap-2 font-semibold text-foreground">
                    <span
                      className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", dotClass)}
                      aria-hidden
                    />
                    <span className="truncate" title={item.label}>
                      {item.label}
                    </span>
                  </span>
                  <span className="flex-shrink-0 font-bold tabular-nums">
                    <span
                      className={cn(
                        actual <= 0 && "text-muted-foreground",
                        rowOver && "text-destructive",
                        !rowOver && actual > 0 && "text-foreground",
                      )}
                    >
                      {formatCurrency(actual)}
                    </span>
                    <span className="mx-1 font-medium text-muted-foreground">/</span>
                    <span className="font-semibold text-muted-foreground">
                      {formatCurrency(budget)}
                    </span>
                    {actual > 0 && rowVariance !== 0 && (
                      <span
                        className={cn(
                          "ml-2 rounded-full px-1.5 py-0.5 text-[11px]",
                          rowOver
                            ? "bg-destructive/15 text-destructive"
                            : "bg-success/15 text-success",
                        )}
                      >
                        {rowOver ? "+" : "−"}
                        {formatCurrency(Math.abs(rowVariance))}
                      </span>
                    )}
                  </span>
                </div>
                <div className="relative h-1.5 w-full overflow-hidden rounded-full border border-border bg-card">
                  <div
                    className="absolute top-0 bottom-0 bg-success transition-all"
                    style={{ left: 0, width: `${rowInBudgetPct}%` }}
                    aria-hidden
                  />
                  <div
                    className="absolute top-0 bottom-0 bg-destructive transition-all"
                    style={{ left: `${rowBudgetPct}%`, width: `${rowOverflowPct}%` }}
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute -top-0.5 -bottom-0.5 w-0.5 bg-muted-foreground/80"
                    style={{ left: `${rowBudgetPct}%` }}
                    aria-hidden
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
